<script lang="ts">
  import { settingsStore, settingsActions } from '$lib/stores/settings'
  import { vaultStore } from '$lib/stores/vaultStore'
  import { contentIndexStore } from '$lib/stores/contentIndexStore'
  import { modal } from '$lib/stores/modalStore'
  import { updateStore, updateActions } from '$lib/stores/updateStore'
  import { subscriptionStore } from '$lib/stores/subscription'
  import { onMount } from 'svelte'
  import { useDebounce, useTimeout } from '$lib/hooks/useTimer'
  import { RefreshCw, Database, Download, ExternalLink } from 'lucide-svelte'
  import { marked } from 'marked'
  import DOMPurify from 'dompurify'
  
  // System settings
  let autoStart = $state(false)
  let autoUpdate = $state(true)
  let allowBetaUpdates = $state(false)
  let showSaveIndicator = $state(false)
  
  // Cache management
  let isReindexing = $state(false)
  let reindexProgress = $state<{ current: number; total: number } | null>(null)
  let indexStats = $state<{ totalDocuments: number; cacheSize: number; lastUpdate?: number } | null>(null)
  
  // Initialize from settings
  onMount(async () => {
    const settings = settingsStore.get()

    // Load saved preferences if they exist
    const savedAutoStart = localStorage.getItem('autoStart')
    const savedAutoUpdate = localStorage.getItem('autoUpdate')
    const savedAllowBetaUpdates = localStorage.getItem('allowBetaUpdates')

    if (savedAutoStart !== null) {
      autoStart = savedAutoStart === 'true'
    }
    if (savedAutoUpdate !== null) {
      autoUpdate = savedAutoUpdate === 'true'
    }
    if (savedAllowBetaUpdates !== null) {
      allowBetaUpdates = savedAllowBetaUpdates === 'true'
    }

    // Load beta updates preference from Electron
    if (typeof window !== 'undefined' && window.api?.getAllowBetaUpdates) {
      try {
        const electronBetaPreference = await window.api.getAllowBetaUpdates()
        allowBetaUpdates = electronBetaPreference
        localStorage.setItem('allowBetaUpdates', String(electronBetaPreference))
      } catch (error) {
        console.error('Failed to load beta updates preference:', error)
      }
    }

    // Load index stats
    updateIndexStats()
  })

  // Auto-enable beta updates for beta tier users
  $effect(() => {
    const subscription = $subscriptionStore

    // If user has beta tier subscription, automatically enable beta updates
    if (subscription?.type === 'beta' && !allowBetaUpdates) {
      allowBetaUpdates = true

      // Save the setting
      localStorage.setItem('allowBetaUpdates', 'true')

      // Save to Electron
      if (typeof window !== 'undefined' && window.api?.setAllowBetaUpdates) {
        window.api.setAllowBetaUpdates(true).catch(error => {
          console.error('Failed to enable beta updates for beta tier:', error)
        })
      }
    }
  })

  // Hide save indicator after 1 second
  const hideSaveIndicator = useTimeout(() => {
    showSaveIndicator = false
  }, 1000, false)
  
  // Debounced save function (500ms delay)
  const saveSettings = useDebounce(async () => {
    // Save to localStorage
    localStorage.setItem('autoStart', String(autoStart))
    localStorage.setItem('autoUpdate', String(autoUpdate))
    localStorage.setItem('allowBetaUpdates', String(allowBetaUpdates))

    // Save beta updates to Electron
    if (typeof window !== 'undefined' && window.api?.setAllowBetaUpdates) {
      try {
        await window.api.setAllowBetaUpdates(allowBetaUpdates)
      } catch (error) {
        console.error('Failed to save beta updates preference:', error)
      }
    }

    // Show save indicator and start hide timer
    showSaveIndicator = true
    hideSaveIndicator.restart()
  }, 500)
  
  function autoSaveSettings() {
    saveSettings()
  }
  
  // Update index statistics
  function updateIndexStats() {
    const state = contentIndexStore.getState()
    if (state?.stats) {
      indexStats = {
        totalDocuments: state.stats.totalDocuments,
        cacheSize: state.stats.cacheSize || 0,
        lastUpdate: state.stats.lastUpdate
      }
    }
  }
  
  // Format date to relative time
  function formatRelativeTime(timestamp: number): string {
    const now = Date.now()
    const diff = now - timestamp
    
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)
    
    if (minutes < 1) return '방금 전'
    if (minutes < 60) return `${minutes}분 전`
    if (hours < 24) return `${hours}시간 전`
    if (days < 7) return `${days}일 전`
    
    // Format as date for older than a week
    const date = new Date(timestamp)
    return date.toLocaleDateString('ko-KR', { 
      month: 'short', 
      day: 'numeric' 
    })
  }
  
  // Handle reindexing
  async function handleReindex() {
    if (isReindexing) return
    
    const confirmed = await modal.confirm({
      title: '인덱스 재생성',
      message: '검색 인덱스를 다시 생성하시겠습니까?\n이 작업은 시간이 걸릴 수 있습니다.',
      confirmText: '재생성',
      cancelText: '취소'
    })
    
    if (!confirmed) return
    
    try {
      isReindexing = true
      reindexProgress = { current: 0, total: 0 }
      
      // Subscribe to progress updates
      const unsubscribe = contentIndexStore.subscribe(state => {
        if (state.progress) {
          reindexProgress = {
            current: state.progress.processed || 0,
            total: state.progress.total || 0
          }
        }
      })
      
      // Perform reindexing
      const totalDocs = await vaultStore.reindexVault()
      
      // Update stats
      updateIndexStats()
      
      // Clean up
      unsubscribe()
      reindexProgress = null
      
      await modal.alert({
        title: '인덱싱 완료',
        message: `${totalDocs}개의 문서가 성공적으로 인덱싱되었습니다.`
      })
    } catch (error) {
      console.error('Reindexing failed:', error)
      await modal.alert({
        title: '오류',
        message: '인덱싱 중 오류가 발생했습니다.\n잠시 후 다시 시도해주세요.'
      })
    } finally {
      isReindexing = false
      reindexProgress = null
    }
  }
  
  
  // Format bytes to human readable
  function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`
  }

  // Render markdown safely
  function renderMarkdown(markdown: string): string {
    if (!markdown) return ''
    const html = marked.parse(markdown, {
      async: false,
      breaks: true,
      gfm: true
    }) as string
    return DOMPurify.sanitize(html)
  }

  // Format last checked time
  function formatLastChecked(timestamp: number): string {
    const now = Date.now()
    const diff = now - timestamp
    const seconds = Math.floor(diff / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (seconds < 60) return '방금 전'
    if (minutes < 60) return `${minutes}분 전`
    if (hours < 24) return `${hours}시간 전`
    if (days < 7) return `${days}일 전`

    const date = new Date(timestamp)
    return `${date.getMonth() + 1}/${date.getDate()}`
  }
</script>

<div class="general-settings">
  <h2 class="section-title">일반 설정</h2>
  <p class="section-description">
    애플리케이션의 기본 동작을 설정합니다.
  </p>
  
  <!-- System Settings -->
  <div class="settings-group">
    <h3 class="group-title">시스템 설정</h3>
    
    <div class="setting-row">
      <div class="setting-info">
        <span class="setting-label">시작 시 자동 실행</span>
        <span class="setting-hint">시스템 시작 시 자동으로 실행됩니다</span>
      </div>
      <label class="toggle">
        <input 
          type="checkbox" 
          bind:checked={autoStart}
          onchange={autoSaveSettings}
        />
        <span class="toggle-slider"></span>
      </label>
    </div>
    
    <div class="setting-row">
      <div class="setting-info">
        <span class="setting-label">자동 업데이트</span>
        <span class="setting-hint">새 버전을 자동으로 설치합니다</span>
      </div>
      <label class="toggle">
        <input
          type="checkbox"
          bind:checked={autoUpdate}
          onchange={autoSaveSettings}
        />
        <span class="toggle-slider"></span>
      </label>
    </div>

    {#if autoUpdate}
      <div class="setting-row sub-setting">
        <div class="setting-info">
          <span class="setting-label">베타 업데이트 받기</span>
          <span class="setting-hint">출시 전 업데이트를 먼저 받아 볼 수 있습니다.</span>
        </div>
        <label class="toggle">
          <input
            type="checkbox"
            bind:checked={allowBetaUpdates}
            onchange={autoSaveSettings}
          />
          <span class="toggle-slider"></span>
        </label>
      </div>
    {/if}
  </div>

  <!-- Update Check -->
  {#if typeof window !== 'undefined' && window.api}
  <div class="settings-group">
    <h3 class="group-title">소프트웨어 업데이트</h3>

    <div class="update-section">
      <div class="update-header">
        <div class="update-info">
          {#if $updateStore.checking}
            <p class="status-text">업데이트 확인 중...</p>
          {:else if $updateStore.error}
            <p class="status-text error">{$updateStore.error}</p>
          {:else if $updateStore.updateAvailable && $updateStore.updateInfo}
            <div class="update-status">
              <div class="status-header">
                <span class="status-badge new">
                  v{$updateStore.updateInfo.version} 사용 가능
                  {#if $updateStore.updateInfo.version.includes('beta') || $updateStore.updateInfo.version.includes('alpha')}
                    <span class="beta-badge">BETA</span>
                  {/if}
                </span>
                <a
                  href="https://github.com/hyungyunlim/korean-grammar-releases/releases/tag/v{$updateStore.updateInfo.version}"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="release-link"
                >
                  <ExternalLink size={12} />
                  릴리즈 보기
                </a>
              </div>
              {#if $updateStore.updateInfo.releaseNotes}
                <div class="release-notes-container">
                  <div class="markdown-preview-content">
                    {@html renderMarkdown($updateStore.updateInfo.releaseNotes)}
                  </div>
                </div>
              {/if}
            </div>
          {:else if $updateStore.updateDownloaded}
            <div class="update-status">
              <span class="status-badge ready">업데이트 준비 완료</span>
            </div>
          {:else if $updateStore.downloading && $updateStore.downloadProgress}
            <div class="update-status">
              <span class="status-text">다운로드 중 {$updateStore.downloadProgress.percent.toFixed(0)}%</span>
              <div class="progress-bar-mini">
                <div class="progress-fill-mini" style="width: {$updateStore.downloadProgress.percent}%"></div>
              </div>
            </div>
          {:else}
            <div class="update-status">
              <div class="status-with-time">
                <span class="status-text">최신 버전 사용 중</span>
                {#if $updateStore.lastChecked}
                  <span class="last-checked">마지막 확인: {formatLastChecked($updateStore.lastChecked)}</span>
                {/if}
              </div>
            </div>
          {/if}
        </div>

        <div class="update-buttons">
          {#if $updateStore.updateDownloaded}
            <button
              class="update-button primary"
              onclick={() => updateActions.installUpdate()}
            >
              <RefreshCw size={14} />
              <span>재시작</span>
            </button>
          {:else if $updateStore.updateAvailable && !$updateStore.downloading}
            <button
              class="update-button primary"
              onclick={() => updateActions.downloadUpdate()}
            >
              <Download size={14} />
              <span>다운로드</span>
            </button>
          {:else}
            <button
              class="update-button"
              onclick={() => updateActions.checkForUpdates()}
              disabled={$updateStore.checking || $updateStore.downloading}
            >
              {#if $updateStore.checking}
                <RefreshCw size={14} class="spinning" />
                <span>확인 중</span>
              {:else}
                <RefreshCw size={14} />
                <span>확인</span>
              {/if}
            </button>
          {/if}
        </div>
      </div>
    </div>
  </div>
  {/if}
  
  <!-- Cache Management -->
  <div class="settings-group">
    <h3 class="group-title">검색 인덱스 관리</h3>
    
    <div class="index-section">
      <div class="index-header">
        <div class="index-info">
          <p class="index-description">
            검색이 제대로 작동하지 않거나 파일이 누락된 경우 인덱스를 재생성하세요.
          </p>
          {#if indexStats}
            <div class="index-stats">
              <span class="stat-item">
                <Database size={14} class="stat-icon" />
                <strong>{indexStats.totalDocuments}</strong> 문서
              </span>
              {#if indexStats.cacheSize > 0}
                <span class="stat-separator">•</span>
                <span class="stat-item">
                  <strong>{formatBytes(indexStats.cacheSize)}</strong> 캐시
                </span>
              {/if}
              {#if indexStats.lastUpdate}
                <span class="stat-separator">•</span>
                <span class="stat-item">
                  업데이트: <strong>{formatRelativeTime(indexStats.lastUpdate)}</strong>
                </span>
              {/if}
            </div>
          {/if}
        </div>
        
        <button
          class="reindex-button"
          onclick={handleReindex}
          disabled={isReindexing}
        >
          {#if isReindexing}
            <RefreshCw size={14} class="icon spinning" />
            {#if reindexProgress}
              <span>인덱싱 중... ({reindexProgress.current}/{reindexProgress.total})</span>
            {:else}
              <span>인덱싱 중...</span>
            {/if}
          {:else}
            <RefreshCw size={14} class="icon" />
            <span>인덱스 재생성</span>
          {/if}
        </button>
      </div>
    </div>
  </div>
  
  <!-- Auto-save indicator -->
  {#if showSaveIndicator}
    <div class="save-indicator">
      <span class="save-icon">✓</span>
      <span>저장됨</span>
    </div>
  {/if}
</div>

<style>
  .general-settings {
    padding: 24px;
    max-width: 800px;
    margin: 0 auto;
  }
  
  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 8px 0;
  }
  
  .section-description {
    font-size: 14px;
    color: var(--text-secondary);
    margin: 0 0 24px 0;
  }
  
  .settings-group {
    padding: 24px 0;
    border-bottom: 1px solid var(--border-primary);
  }
  
  .settings-group:last-child {
    border-bottom: none;
  }
  
  .group-title {
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 20px 0;
  }
  
  /* System Settings */
  .setting-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0;
  }
  
  .setting-row + .setting-row {
    border-top: 1px solid var(--divider);
  }

  .setting-row.sub-setting {
    padding-left: 24px;
    padding-top: 12px;
    padding-bottom: 12px;
    border-radius: 6px;
    margin-top: 8px;
    border-top: none;
  }

  .setting-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  
  .setting-label {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-primary);
  }
  
  .setting-hint {
    font-size: 12px;
    color: var(--text-secondary);
  }
  
  /* Toggle Switch */
  .toggle {
    position: relative;
    display: inline-block;
    width: 44px;
    height: 24px;
    cursor: pointer;
  }
  
  .toggle input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  .toggle-slider {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--border-secondary);
    border-radius: 24px;
    transition: all 0.2s;
  }
  
  .toggle-slider:before {
    position: absolute;
    content: "";
    height: 20px;
    width: 20px;
    left: 2px;
    bottom: 2px;
    background: white;
    border-radius: 50%;
    transition: all 0.2s;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  }
  
  .toggle input:checked + .toggle-slider {
    background: var(--accent-primary);
  }
  
  .toggle input:checked + .toggle-slider:before {
    transform: translateX(20px);
  }
  
  /* Update Section */
  .update-section {
    background: var(--bg-secondary);
    border: 1px solid var(--border-primary);
    border-radius: 8px;
    padding: 14px 16px;
  }

  .update-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }

  .update-info {
    flex: 1;
  }

  .update-status {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .status-header {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .status-badge {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: var(--ui-font-size-sm);
    font-weight: 500;
    width: fit-content;
  }

  .status-badge.new {
    background: var(--bg-tertiary);
    color: var(--accent-primary);
  }

  .status-badge.ready {
    background: var(--bg-tertiary);
    color: var(--success);
  }

  .beta-badge {
    display: inline-block;
    margin-left: 6px;
    padding: 1px 5px;
    font-size: 10px;
    font-weight: 600;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 3px;
    letter-spacing: 0.5px;
  }

  .status-text {
    font-size: var(--ui-font-size-sm);
    color: var(--text-secondary);
  }

  .status-text.error {
    color: var(--error);
  }

  .status-with-time {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .last-checked {
    font-size: var(--ui-font-size-xs);
    color: var(--text-tertiary);
  }

  .release-link {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: var(--ui-font-size-xs);
    color: var(--text-secondary);
    text-decoration: none;
    transition: color 0.15s;
  }

  .release-link:hover {
    color: var(--accent-primary);
  }

  .release-notes-container {
    max-height: 150px;
    overflow-y: auto;
  }

  .release-notes-container::-webkit-scrollbar {
    width: 4px;
  }

  .release-notes-container::-webkit-scrollbar-track {
    background: transparent;
  }

  .release-notes-container::-webkit-scrollbar-thumb {
    background: var(--border-secondary);
    border-radius: 2px;
  }

  .release-notes-container::-webkit-scrollbar-thumb:hover {
    background: var(--text-tertiary);
  }

  /* Markdown content styles - adapted from MarkdownEditor */
  .markdown-preview-content {
    font-size: var(--ui-font-size-sm);
    line-height: 1.6;
    color: var(--text-secondary);
  }

  .markdown-preview-content :global(h1),
  .markdown-preview-content :global(h2),
  .markdown-preview-content :global(h3),
  .markdown-preview-content :global(h4) {
    font-size: var(--ui-font-size-md);
    font-weight: 600;
    color: var(--text-primary);
    margin: 8px 0 4px 0;
  }

  .markdown-preview-content :global(h1:first-child),
  .markdown-preview-content :global(h2:first-child),
  .markdown-preview-content :global(h3:first-child),
  .markdown-preview-content :global(h4:first-child) {
    margin-top: 0;
  }

  .markdown-preview-content :global(p) {
    margin: 4px 0;
  }

  .markdown-preview-content :global(ul),
  .markdown-preview-content :global(ol) {
    margin: 4px 0;
    padding-left: 20px;
  }

  .markdown-preview-content :global(ul) {
    list-style-type: disc;
  }

  .markdown-preview-content :global(ol) {
    list-style-type: decimal;
  }

  .markdown-preview-content :global(li) {
    margin: 2px 0;
    display: list-item;
  }

  .markdown-preview-content :global(code) {
    background: var(--bg-tertiary);
    padding: 2px 4px;
    border-radius: 3px;
    font-size: var(--ui-font-size-xs);
    font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
    color: var(--text-primary);
  }

  .markdown-preview-content :global(pre) {
    background: var(--bg-tertiary);
    padding: 8px;
    border-radius: 4px;
    overflow-x: auto;
    margin: 4px 0;
  }

  .markdown-preview-content :global(pre code) {
    background: none;
    padding: 0;
  }

  .markdown-preview-content :global(a) {
    color: var(--accent-primary);
    text-decoration: none;
  }

  .markdown-preview-content :global(a:hover) {
    text-decoration: underline;
  }

  .markdown-preview-content :global(strong) {
    font-weight: 600;
    color: var(--text-primary);
  }

  .markdown-preview-content :global(em) {
    font-style: italic;
  }

  .markdown-preview-content :global(blockquote) {
    border-left: 2px solid var(--border-secondary);
    padding-left: 12px;
    margin: 4px 0;
    color: var(--text-tertiary);
    font-style: italic;
  }

  .progress-bar-mini {
    width: 200px;
    height: 3px;
    background: var(--bg-tertiary);
    border-radius: 2px;
    overflow: hidden;
  }

  .progress-fill-mini {
    height: 100%;
    background: var(--accent-primary);
    transition: width 0.3s ease;
  }

  .update-buttons {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex-shrink: 0;
  }

  .update-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: var(--ui-font-size-md);
    font-weight: 500;
    border: 1px solid var(--border-primary);
    background: var(--btn-secondary-bg);
    color: var(--text-primary);
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
  }

  .update-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .update-button:hover:not(:disabled) {
    background: var(--bg-secondary);
    border-color: var(--accent-primary);
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }

  .update-button.primary {
    background: var(--btn-primary-bg);
    color: var(--btn-primary-text);
    border-color: var(--btn-primary-bg);
  }

  .update-button.primary:hover:not(:disabled) {
    background: var(--btn-primary-hover);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  }

  .update-button:active:not(:disabled) {
    transform: scale(0.98);
  }

  /* Make icons white in primary buttons */
  :global(.update-button.primary svg) {
    color: white;
  }

  :global(.update-button .spinning) {
    animation: spin 1s linear infinite;
  }

  /* Index Section */
  .index-section {
    border: 1px solid var(--border-primary);
    border-radius: 8px;
    padding: 16px 20px;
  }
  
  .index-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
  }
  
  .index-info {
    flex: 1;
  }
  
  .index-description {
    font-size: 13px;
    color: var(--text-secondary);
    line-height: 1.5;
    margin: 0 0 12px 0;
  }
  
  .index-stats {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: var(--text-tertiary);
  }
  
  .stat-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  
  .stat-item strong {
    font-weight: 600;
    color: var(--text-primary);
  }
  
  .stat-separator {
    color: var(--text-tertiary);
    opacity: 0.5;
  }
  
  :global(.stat-icon) {
    color: var(--text-tertiary);
    opacity: 0.6;
    flex-shrink: 0;
  }
  
  .reindex-button {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: var(--ui-font-size-md);
    font-weight: 500;
    border: none;
    background: var(--btn-primary-bg);
    color: var(--btn-primary-text);
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
    flex-shrink: 0;
  }
  
  .reindex-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .reindex-button:hover:not(:disabled) {
    background: var(--btn-primary-hover);
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
  
  .reindex-button:active:not(:disabled) {
    transform: scale(0.98);
  }
  
  :global(.reindex-button svg) {
    color: white;
  }

  :global(.reindex-button .spinning) {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  /* Responsive layout */
  @media (max-width: 768px) {
    .index-header {
      flex-direction: column;
      gap: 12px;
    }
    
    .reindex-button {
      align-self: flex-end;
    }
  }
  
  @media (max-width: 480px) {
    .reindex-button {
      width: 100%;
      justify-content: center;
    }
    
    .index-stats {
      flex-wrap: wrap;
    }
  }
  
  /* Save Indicator */
  .save-indicator {
    position: fixed;
    bottom: 20px;
    right: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: var(--success);
    color: white;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    animation: slideIn 0.3s ease;
    z-index: 1000;
  }
  
  .save-icon {
    font-size: 14px;
    font-weight: bold;
  }
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* Dark theme adjustments */
  :global(.dark-theme) .reindex-button {
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  }

  :global(.dark-theme) .reindex-button:hover:not(:disabled) {
    box-shadow: 0 2px 8px rgba(0,0,0,0.4);
  }
</style>